import type { TreeNode } from '@mona-roadmap/domain';
import { SearchX } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { RoadmapModel } from '../hooks/useRoadmapModel';
import { gridTemplate, type ColumnDef, type ColumnKey } from '../lib/columns';
import { isTypingTarget, navAction } from '../lib/keyboardNav';
import type { RevealRequest } from '../lib/mutations';
import { isTempId } from '../lib/optimistic';
import { GridHeader } from './GridHeader';
import { useRoadmapContext } from './RoadmapContext';
import { TreeRow } from './TreeRow';
import type { FilterState, SortKey, SortState } from '@mona-roadmap/domain';

interface Props {
  model: RoadmapModel;
  columns: ColumnDef[];
  filters: FilterState;
  sort: SortState;
  onSort: (key: SortKey) => void;
  onOpenFilter: (column: ColumnKey, anchor: HTMLElement) => void;
  onClearFilters: () => void;
  setExpanded: (keys: string[], open: boolean) => void;
  reveal: RevealRequest | null;
  /** Reports whether a reveal request could be satisfied (false: item not visible in this view). */
  onRevealResult: (found: boolean) => void;
}

const selectorFor = (key: string) => `[role="row"][data-key="${CSS.escape(key)}"]`;

function findPath(roots: TreeNode[], match: (n: TreeNode) => boolean): TreeNode[] | null {
  for (const n of roots) {
    if (match(n)) return [n];
    const sub = findPath(n.children, match);
    if (sub) return [n, ...sub];
  }
  return null;
}

export function TreeGrid({ model, columns, filters, sort, onSort, onOpenFilter, onClearFilters, setExpanded, reveal, onRevealResult }: Props) {
  const ctx = useRoadmapContext();
  const { rows, roots, filtersActive } = model;
  const gridRef = useRef<HTMLDivElement>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [flashKey, setFlashKey] = useState<string | null>(null);
  const [renamingKey, setRenamingKey] = useState<string | null>(null);
  const pendingFocus = useRef<string | null>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const selectedRef = useRef(selectedKey);
  selectedRef.current = selectedKey;

  const template = useMemo(() => gridTemplate(columns), [columns]);
  const tabStop = useMemo(() => {
    if (selectedKey && rows.some((r) => r.node.key === selectedKey)) return selectedKey;
    return rows[0]?.node.key ?? null;
  }, [rows, selectedKey]);

  const focusRow = useCallback((key: string) => {
    pendingFocus.current = key;
    setSelectedKey(key);
  }, []);

  // Move DOM focus after the row exists (it may only appear after an expansion re-render).
  useLayoutEffect(() => {
    const key = pendingFocus.current;
    if (!key) return;
    const el = gridRef.current?.querySelector<HTMLElement>(selectorFor(key));
    if (!el) return;
    pendingFocus.current = null;
    el.focus({ preventScroll: true });
    el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [rows, selectedKey, renamingKey]);

  // Reveal a freshly created item: expand its ancestors, focus + flash its row.
  useEffect(() => {
    if (!reveal) return;
    const path = findPath(roots, (n) => n.kind === reveal.kind && n.entityId === reveal.id);
    if (path) {
      const node = path[path.length - 1]!;
      setExpanded(path.slice(0, -1).map((n) => n.key), true);
      if (reveal.focus !== false) focusRow(node.key);
      else setSelectedKey(node.key);
      setFlashKey(node.key);
      onRevealResult(true);
      const t = setTimeout(() => setFlashKey((k) => (k === node.key ? null : k)), 1900);
      return () => clearTimeout(t);
    }
    // Not visible yet (cache/render lag) or hidden by filters / the other view: give it a moment.
    const t = setTimeout(() => onRevealResult(false), 450);
    return () => clearTimeout(t);
  }, [reveal, roots, setExpanded, focusRow, onRevealResult]);

  const rowByKey = useCallback((key: string) => rows.find((r) => r.node.key === key), [rows]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (!gridRef.current?.contains(target)) return; // events bubbling through React portals (popovers, dialogs)
    if (isTypingTarget(target)) return;
    const rowEl = target.closest<HTMLElement>('[role="row"][data-key]');
    const key = rowEl?.dataset.key;
    if (!key) return;
    if ((target.tagName === 'BUTTON' || target.tagName === 'A') && (e.key === 'Enter' || e.key === ' ')) return; // native activation
    if (e.code === 'KeyQ' && !e.shiftKey) {
      // Quick filter: show only the focused goal / epic (again to clear it).
      const node = rowByKey(key)?.node;
      if (node && (node.kind === 'goal' || node.kind === 'epic')) {
        e.preventDefault();
        ctx.quickFilter(node);
      }
      return;
    }
    const action = navAction(e.key, rows, key);
    if (!action) return;
    e.preventDefault();
    switch (action.type) {
      case 'focus':
        focusRow(action.key);
        break;
      case 'expand':
        setExpanded([action.key], true);
        break;
      case 'collapse':
        setExpanded([action.key], false);
        break;
      case 'toggle':
        setExpanded([action.key], !rowByKey(action.key)?.expanded);
        break;
      case 'open': {
        const row = rowByKey(action.key);
        if (!row) break;
        if (row.node.kind === 'standalone' || row.node.kind === 'no-goal') setExpanded([row.node.key], !row.expanded);
        else ctx.openNode(row.node);
        break;
      }
      case 'rename': {
        const node = rowByKey(action.key)?.node;
        if (node && node.entityId && !isTempId(node.entityId) && node.kind !== 'standalone' && node.kind !== 'no-goal') setRenamingKey(node.key);
        break;
      }
    }
  };

  const toggle = useCallback((key: string) => setExpanded([key], !rowsRef.current.find((r) => r.node.key === key)?.expanded), [setExpanded]);
  const startRename = useCallback((key: string) => setRenamingKey(key), []);
  // After a rename ends the input unmounts; hand focus back to the row so keyboard navigation continues.
  const stopRename = useCallback(() => {
    pendingFocus.current = selectedRef.current;
    setRenamingKey(null);
  }, []);

  let deliverableIndex = -1; // position of a deliverable within its group, for the zebra striping
  return (
    <div className="tg-scroll" data-testid="grid-scroll">
      <div ref={gridRef} role="treegrid" aria-label="Roadmap" aria-rowcount={rows.length + 1} aria-colcount={columns.length} className="tg" style={{ ['--cols' as string]: template }} onKeyDown={onKeyDown}>
        <div role="rowgroup" className="tg-headgroup">
          <GridHeader columns={columns} filters={filters} filtersActive={filtersActive} sort={sort} onSort={onSort} onOpenFilter={onOpenFilter} />
        </div>
        <div role="rowgroup" className="tg-body">
          {rows.map((r) => {
            if (r.node.kind === 'deliverable') deliverableIndex++;
            else deliverableIndex = -1; // a group header restarts the striping: the first deliverable is always white
            return (
            <TreeRow
              key={r.node.key}
              node={r.node}
              hasChildren={r.hasChildren}
              expanded={r.expanded}
              selected={selectedKey === r.node.key}
              tabStop={tabStop === r.node.key}
              stripe={r.node.kind === 'deliverable' && deliverableIndex % 2 === 1}
              flash={flashKey === r.node.key}
              renaming={renamingKey === r.node.key}
              columns={columns}
              onFocusRow={setSelectedKey}
              onToggle={toggle}
              onStartRename={startRename}
              onStopRename={stopRename}
            />
            );
          })}
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="sticky left-0 flex flex-col items-center gap-2 px-6 py-16 text-center" role="status" data-testid="empty-state">
          <SearchX className="size-6 text-faint" aria-hidden />
          {filtersActive ? (
            <>
              <p className="text-[16px] font-medium">No deliverables match the filters</p>
              <p className="text-dim">Try removing a filter or changing the search.</p>
              <button type="button" className="btn btn-primary" onClick={onClearFilters}>
                Clear All Filters
              </button>
            </>
          ) : (
            <>
              <p className="text-[16px] font-medium">Nothing here yet</p>
              <p className="text-dim">Create a product goal or a deliverable to get started.</p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
